import { ApiError } from '@/api/errors';

// A 409 from an org-entry endpoint (switch-org or login select-org): the target
// org needs a fresh SSO login. The session is already revoked, so the caller must
// redirect to `ssoURL`. Extends ApiError (status 409) so it counts as an expected
// error, not a reportable failure. Lives in its own module so both the switch-org
// api and the cookie login api can import it without a circular dependency.
export class ReauthRequiredError extends ApiError {
  constructor(readonly ssoURL: string) {
    super('Org switch requires re-authentication', { status: 409 });
    this.name = 'ReauthRequiredError';
  }
}

// An org-entry endpoint returns 409 when the target org needs a fresh SSO login.
// The 409 is deliberately not 401/403, so it bypasses fetchClient's logout
// interceptor and arrives as a plain ApiError. Returns a ReauthRequiredError when
// the error is such a 409 with a usable ssoURL, else undefined; callers throw
// `toReauthRequiredError(err) ?? err` to keep the original error otherwise.
export function toReauthRequiredError(err: unknown): ReauthRequiredError | undefined {
  if (err instanceof ApiError && err.status === 409) {
    const data = err.response?.data as { ssoURL?: unknown } | undefined;
    if (typeof data?.ssoURL === 'string' && data.ssoURL !== '') {
      return new ReauthRequiredError(data.ssoURL);
    }
  }
  return undefined;
}
